import { UIData } from '../../hooks/stores/uiStore';
import { LauncherElementData, TreeNode } from '../../hooks/stores/launcherElementsStore';
import { LauncherWindowData } from '../../hooks/stores/launcherWindowStore';
import { findParentID } from '../../functions/launcherElement/findParentID';
import { findNodeIndex } from '../../functions/launcherElement/findNodeIndex';
import { flattenTree } from '../../functions/launcherConfig/flattenTree';
import { cleanTree } from '../../functions/launcherConfig/cleanTree';
import { moveNodesToParentByID } from '../../functions/launcherElement/moveNodesToParentByID';
import { updateObjectNestedProperties } from '../../functions/launcherElement/updateObjectNestedProperties';
import { changeIDsRecursively } from '../../functions/launcherElement/changeIDsRecursively';

interface CopyPasteUtilsDependencies {
  UIData: UIData;
  setUIData: (data: Partial<UIData>) => void;
  launcherWindowData: LauncherWindowData;
  setLauncherWindowData: (data: Partial<LauncherWindowData>, replace?: boolean) => void;
  launcherElementsData: Record<string, LauncherElementData>;
  setLauncherElementsData: (data: Partial<Record<string, LauncherElementData>>, replace?: boolean) => void;
  copiedElements: LauncherElementData[] | undefined;
  setCopiedElements: (elements: LauncherElementData[] | undefined) => void;
}

export function createCopyPasteUtils({
  UIData,
  setUIData,
  launcherWindowData,
  setLauncherWindowData,
  launcherElementsData,
  setLauncherElementsData,
  copiedElements,
  setCopiedElements,
}: CopyPasteUtilsDependencies) {
  const duplicateSelectedElements = (): LauncherElementData[] => {
    const newElements: LauncherElementData[] = [];

    let elementsData = { ...launcherElementsData };
    let windowData = { ...launcherWindowData };

    UIData.selectedElementIDs.forEach((id) => {
      const newElement = JSON.parse(JSON.stringify(launcherElementsData[id]));
      const parentId = findParentID(windowData.children, id) || undefined;
      const index = findNodeIndex(windowData.children, id) || 0;
      changeIDsRecursively(newElement);

      elementsData = {
        ...elementsData,
        [newElement.id]: newElement,
        ...(flattenTree([newElement], (node) => node?.props?.children) as {
          [id: string]: LauncherElementData;
        }),
      };

      windowData = {
        ...windowData,
        children: [
          ...windowData.children,
          ...cleanTree([newElement], (node) => node?.props?.children),
        ],
      };

      windowData.children = moveNodesToParentByID({
        tree: windowData.children,
        nodeIDs: [newElement.id],
        parentID: parentId,
        index: index + 1,
      });

      newElements.push(newElement);
    });

    setLauncherElementsData({ ...elementsData }, true);
    setLauncherWindowData({ ...windowData }, true);

    setUIData({
      selectedElementIDs: [...newElements.map((element) => element.id)],
      elementInEditingModeID: undefined,
    });

    return newElements;
  };

  const copySelectedElements = () => {
    const elements = UIData.selectedElementIDs.map(
      (id) => launcherElementsData[id]
    );
    setCopiedElements(elements);
  };

  const pasteElements = () => {
    if (!copiedElements) return;
    if (UIData.selectedElementIDs.length > 1) return;

    const newElements: LauncherElementData[] = [];
    let elementsData = { ...launcherElementsData };
    let windowData = { ...launcherWindowData };

    copiedElements.forEach((element) => {
      let newElement = JSON.parse(JSON.stringify(element));
      changeIDsRecursively(newElement);

      newElement = updateObjectNestedProperties(
        newElement,
        {
          position: 'relative',
          top: undefined,
          bottom: undefined,
          left: undefined,
          right: undefined,
        },
        'props.class'
      );

      elementsData = {
        ...elementsData,
        [newElement.id]: newElement,
        ...(flattenTree([newElement], (node) => node?.props?.children) as {
          [id: string]: LauncherElementData;
        }),
      };

      windowData = {
        ...windowData,
        children: [
          ...windowData.children,
          ...cleanTree([newElement], (node) => node?.props?.children),
        ],
      };

      windowData.children = moveNodesToParentByID({
        tree: windowData.children,
        nodeIDs: [newElement.id],
        parentID: UIData.selectedElementIDs[0],
      });

      newElements.push(newElement);
    });

    setLauncherElementsData({ ...elementsData }, true);
    setLauncherWindowData({ ...windowData }, true);

    setUIData({
      selectedElementIDs: [...newElements.map((element) => element.id)],
      elementInEditingModeID: undefined,
    });
  };

  return {
    duplicateSelectedElements,
    copySelectedElements,
    pasteElements,
  };
} 